import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, CloudUpload, EyeOff, FileLock, FileText, Images, Mail, MonitorSmartphone, Trash2, User, ExternalLink } from "lucide-react";

import { api, openExternal } from "@/lib/api";
import { Button, ScreenHeader } from "@/components/kit";
import { Page } from "@/components/layout";
import { Section, Toggle } from "@/admin/kit";
import { useApp } from "@/store";

const GOOGLE_TERMS = "https://ai.google.dev/gemini-api/terms";

function Bullet({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-3">
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-ocean" aria-hidden />
      <div className="flex flex-1 flex-col gap-0.5"><p className="t-strong">{title}</p><p className="t-body">{body}</p></div>
    </div>
  );
}

export default function Privacy() {
  const navigate = useNavigate();
  const { mode, analyticsOn, setAnalytics, rememberScan, setRememberScan, clearScanHistory, clearDeviceData, setTryOn, setScanPhoto, tryOn, scanPhoto, toast } = useApp();
  const [contact, setContact] = useState("privacy@breez.au");
  const [camPerm, setCamPerm] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    api("/legal").then((l) => setContact(l.privacy_contact)).catch(() => {});
    navigator.permissions?.query({ name: "camera" }).then((p) => setCamPerm(p.state)).catch(() => setCamPerm(null));
  }, []);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast(msg, "success");
    } catch (e) {
      toast(e.message, "error");
    }
  };
  const permLabel = { granted: "Allowed", denied: "Blocked", prompt: "Not asked yet" }[camPerm] ?? "Managed by your browser";

  return (
    <div data-testid="privacy-screen">
      <ScreenHeader testId="privacy-screen-back" label="BREEZ" fallback="/mystyle" />
      <Page wide className="pb-12">
        <h1 className="t-h1 mb-4">Privacy centre</h1>
        <div className="grid gap-4 md:grid-cols-2">
          <Section title="How your photos are handled" testId="privacy-photos">
            <Bullet icon={CloudUpload} title="Sent through our server to Google Gemini" body="Only to create your hair estimate (scan) or your hairstyle preview (try-on). Your photo goes from the app to the BREEZ server, then to Google's Gemini API." />
            <Bullet icon={Trash2} title="Not stored by us" body="Photos are processed in memory and discarded. They aren't saved on our server or in your account." />
            <Bullet icon={EyeOff} title="Not used to identify you" body="We don't use photos for face recognition, emotion analysis or training AI models." />
            <Bullet icon={FileText} title="Google's API terms apply" body="Google processes the request under its Gemini API data terms." />
            <Button testId="privacy-google-terms" title="Google Gemini API terms" icon={ExternalLink} variant="ghost" onClick={() => openExternal(GOOGLE_TERMS)} className="self-start" />
          </Section>
          <Section title="What we keep" testId="privacy-data">
            <Bullet icon={User} title="With an account" body="Email, display name, a securely hashed password, your hair profile and scan estimates (text only, never photos) and saved styles — until you delete your account." />
            <Bullet icon={MonitorSmartphone} title="As a guest" body="Your profile, saves and cart link stay in this browser only." />
            <Bullet icon={BarChart3} title="Anonymous analytics" body="Counts like 'style viewed' or 'scan completed' tied to a random install id — no name, email, IP address or photos." />
          </Section>
          <Section title="Your choices" testId="privacy-choices">
            <Toggle testId="privacy-analytics-toggle" label="Anonymous analytics" value={analyticsOn} onChange={(v) => act(() => setAnalytics(v), v ? "Analytics on." : "Analytics off — no events are sent.")} />
            <Toggle testId="privacy-remember-scan-toggle" label="Remember my scan results" value={rememberScan} onChange={(v) => act(() => setRememberScan(v), v ? "Scan results will be saved to your profile." : "New scan results are kept for this session only.")} />
            <p className="t-caption">When “Remember my scan results” is off, new scan estimates are used for this session only and aren&apos;t saved.</p>
            <Button testId="privacy-delete-scan" title="Delete scan history" icon={Trash2} variant="outline" onClick={() => act(clearScanHistory, "Scan history deleted.")} />
            <Button testId="privacy-delete-previews" title="Delete previews & images held in the app" icon={Images} variant="outline" onClick={() => act(() => { setTryOn(null); setScanPhoto(null); }, "Previews and images removed from the app.")} />
            <p data-testid="privacy-previews-status" className="t-caption">{tryOn || scanPhoto ? "A scan selfie or try-on preview is currently held in the app's memory." : "No scan selfie or try-on preview is held in the app."} Previews you downloaded stay on your device.</p>
          </Section>
          <div className="flex flex-col gap-4">
            <Section title="Camera & photos permissions" testId="privacy-permissions">
              <p data-testid="privacy-perm-camera" className="t-body">Camera: {permLabel}</p>
              <p className="t-body">Your browser manages camera and file access. To change it, click the camera or lock icon in the address bar and update the Camera setting for this site. Uploading a photo never needs camera access.</p>
            </Section>
            <Section title="This device" testId="privacy-device">
              <p className="t-body">Clear everything this app keeps in this browser: profile, saved styles, scan history, cart link and previews.{mode === "user" ? " Your account data on our server isn't affected — use Account to delete it." : ""}</p>
              {confirmClear ? (
                <div className="flex gap-3">
                  <Button testId="privacy-clear-device-confirm" title="Clear now" icon={Trash2} onClick={() => act(async () => { await clearDeviceData(); setConfirmClear(false); }, "All app data on this device was cleared.")} className="flex-1" />
                  <Button testId="privacy-clear-device-cancel" title="Cancel" variant="outline" onClick={() => setConfirmClear(false)} className="flex-1" />
                </div>
              ) : <Button testId="privacy-clear-device" title="Clear all my data on this device" icon={MonitorSmartphone} variant="outline" onClick={() => setConfirmClear(true)} />}
              {mode === "user" ? <Button testId="privacy-account" title="Account & delete account" icon={User} variant="ghost" onClick={() => navigate("/account")} /> : null}
            </Section>
            <Section title="Policies & contact">
              <Button testId="privacy-terms-link" title="Terms of use" icon={FileText} variant="ghost" onClick={() => navigate("/legal/terms")} className="self-start" />
              <Button testId="privacy-policy-link" title="Privacy policy" icon={FileLock} variant="ghost" onClick={() => navigate("/legal/privacy_policy")} className="self-start" />
              <p className="t-body">Privacy requests:</p>
              <a data-testid="privacy-contact" href={`mailto:${contact}`} className="focus-ring flex min-h-[44px] items-center gap-2 self-start rounded-md t-strong !text-navy underline"><Mail className="h-[18px] w-[18px]" aria-hidden />{contact}</a>
              <p className="t-caption">AI styling suggestions are estimates, not professional or medical assessments.</p>
            </Section>
          </div>
        </div>
      </Page>
    </div>
  );
}
